import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { router, useLocalSearchParams } from 'expo-router';
import { initStripe, useStripe } from '@/services/stripePaymentSheet';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { format, parseISO } from 'date-fns';
import { Path, Svg } from 'react-native-svg';

import PageHeader from '@/components/PageHeader';
import PrimaryButton from '@/components/PrimaryButton';
import SponsorAppointmentSummaryCard from '@/components/SponsorAppointmentSummaryCard';
import { useLanguage } from '@/contexts/LanguageContext';
import OneSignalService from '@/services/oneSignalService';
import { profileService } from '@/services/profileService';
import {
  createAppointmentBookingSession,
  getAppointmentBookingQuote,
  getAppointmentBookingSession,
  getStripePublishableKey,
  resumeAppointmentBookingSession,
} from '@/services/sponsorService';
import { formatMinorCurrency } from '@/components/ProfessionalDetails';

type PaymentMethod = 'pix' | 'card';

function firstRouteParam(value: string | string[] | undefined): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}

const PixIcon = () => (
  <Svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <Path
      d='M10.1 12.33L12.37 14.59C12.62 14.84 12.91 15.05 13.22 15.22L11.17 17.27C10.53 17.91 9.48 17.91 8.83 17.27L6.86 15.3C7.22 15.12 7.55 14.89 7.84 14.6L10.1 12.33ZM10 2.25C10.39 2.25 10.76 2.38 11.05 2.63L13.22 4.78C12.91 4.95 12.62 5.16 12.38 5.41L10.11 7.67L7.84 5.4C7.55 5.11 7.22 4.88 6.86 4.7L8.83 2.74C9.14 2.43 9.56 2.25 10 2.25Z'
      fill='#151515'
    />
    <Path
      d='M5.31 7.12C5.52 7.12 5.73 7.21 5.88 7.36L8.52 10L5.88 12.64C5.73 12.79 5.52 12.87 5.31 12.87H4.44L2.73 11.17C2.09 10.52 2.09 9.47 2.73 8.83L4.44 7.12H5.31ZM14.9 7.13H15.57L17.26 8.83C17.91 9.47 17.91 10.52 17.26 11.17L15.57 12.86H14.9C14.69 12.86 14.48 12.78 14.33 12.63L11.7 10L14.33 7.37C14.48 7.22 14.69 7.13 14.9 7.13Z'
      fill='#151515'
    />
  </Svg>
);

const CardIcon = () => (
  <Svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <Path
      d='M2.5 17.07H16.99M4.54 13.28L13.73 4.09M9.39 13.87L10.91 12.35'
      stroke='#151515'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <Path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M9 3.41L3.86 8.55C2.62 9.79 2.72 11.45 3.96 12.7L7.32 16.06C8.57 17.31 10.23 17.41 11.48 16.17L16.61 11.03C17.86 9.78 17.76 8.12 16.51 6.88L13.15 3.51C11.9 2.27 10.25 2.16 9 3.41Z'
      stroke='#151515'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </Svg>
);

export default function SponsorPayment() {
  const { t, language } = useLanguage();
  const insets = useSafeAreaInsets();
  const submittingRef = useRef(false);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>('card');
  const [submitting, setSubmitting] = useState(false);
  const [officialQuote, setOfficialQuote] = useState<{
    amountMinor: number;
    currency: string;
  } | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteUnavailable, setQuoteUnavailable] = useState(false);
  const [refundOutcome, setRefundOutcome] = useState<'started' | 'completed' | null>(null);
  const { initPaymentSheet, presentPaymentSheet } = useStripe();
  const {
    sponsorId,
    sponsorName,
    sponsorPhotoUrl,
    sponsorRole,
    scheduledDate,
    scheduledTime,
    amountMinor,
    currency,
    bookingSessionId,
  } = useLocalSearchParams<{
    sponsorId: string;
    sponsorName: string;
    sponsorPhotoUrl?: string;
    sponsorRole?: string;
    scheduledDate: string;
    scheduledTime: string;
    amountMinor?: string | string[];
    currency?: string | string[];
    bookingSessionId?: string | string[];
  }>();
  const initialBookingSessionId = firstRouteParam(bookingSessionId);
  const activeBookingSessionIdRef = useRef<string | null>(
    initialBookingSessionId && initialBookingSessionId.length > 0 ? initialBookingSessionId : null
  );

  const locale = language.startsWith('pt')
    ? 'pt-BR'
    : language.startsWith('es')
      ? 'es-ES'
      : 'en-US';
  const amountMinorParam = firstRouteParam(amountMinor)?.trim();
  const currencyParam = firstRouteParam(currency)?.trim();
  const amount =
    amountMinorParam && /^\d+$/.test(amountMinorParam) ? Number(amountMinorParam) : null;
  const routePrice = formatMinorCurrency(amount, currencyParam, locale);
  const formattedPrice =
    formatMinorCurrency(
      officialQuote?.amountMinor ?? amount,
      officialQuote?.currency ?? currencyParam,
      locale
    ) ?? routePrice;
  const appointmentDateLabel = useMemo(() => {
    try {
      return format(parseISO(scheduledDate), 'dd/MM');
    } catch {
      return '';
    }
  }, [scheduledDate]);

  const roleLabel = useMemo(() => {
    const role = (sponsorRole || '').trim().toLowerCase();
    if (role.includes('madrinha')) return t('sponsors.list.madrinha');
    if (role.includes('padrinho')) return t('sponsors.list.padrinho');
    return sponsorRole || '';
  }, [sponsorRole, t]);

  const isValidBookingContext =
    typeof sponsorId === 'string' &&
    sponsorId.length > 0 &&
    /^\d{4}-\d{2}-\d{2}$/.test(scheduledDate || '') &&
    /^\d{2}:\d{2}$/.test(scheduledTime || '');

  useEffect(() => {
    if (!isValidBookingContext) return;
    let active = true;
    setQuoteLoading(true);
    setQuoteUnavailable(false);

    getAppointmentBookingQuote({ sponsorId, scheduledDate, scheduledTime })
      .then((quote) => {
        if (!active) return;
        setOfficialQuote({ amountMinor: quote.amountMinor, currency: quote.currency });
      })
      .catch(() => {
        // The Agenda already supplied an Elo-authoritative display price.
        // Checkout revalidates it again before creating the PaymentIntent, so
        // do not add a second 15-second availability request on this screen.
        if (active && !routePrice) setQuoteUnavailable(true);
      })
      .finally(() => {
        if (active) setQuoteLoading(false);
      });

    return () => {
      active = false;
    };
  }, [isValidBookingContext, routePrice, scheduledDate, scheduledTime, sponsorId]);

  const navigateAfterScheduling = async () => {
    let hasPermission = true;
    if (Platform.OS !== 'web') {
      try {
        const status = await OneSignalService.getPermissionStatus();
        hasPermission = status?.hasNotificationPermission ?? true;
      } catch {
        hasPermission = true;
      }
    }

    router.replace({
      pathname: hasPermission ? '/sponsors/success' : '/sponsors/enable-notifications',
      params: { sponsorName },
    });
  };

  const logCheckoutFailure = (step: string, errorCode: unknown) => {
    const code =
      typeof errorCode === 'string' && errorCode.length > 0 ? errorCode.slice(0, 120) : 'unknown';
    import('@/utils/clarityEvents')
      .then(({ logClarityEvent }) =>
        logClarityEvent('appointment_checkout_failed', {
          method: 'card',
          step,
          error_code: code,
        })
      )
      .catch(() => {});
  };

  const handleConfirm = async () => {
    if (submittingRef.current || !isValidBookingContext) {
      if (!isValidBookingContext) {
        Alert.alert(t('sponsors.errors.title'), t('sponsors.payment.invalidContext'));
      }
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    try {
      const profile = await profileService.getProfile();
      if (!profile?.phone) {
        Alert.alert(t('sponsors.errors.title'), t('sponsors.payment.phoneMissing'));
        return;
      }

      if (Platform.OS === 'web') {
        throw new Error('PAYMENT_SHEET_UNAVAILABLE');
      }
       const resumableSessionId = activeBookingSessionIdRef.current;
       const checkout = resumableSessionId
         ? await resumeAppointmentBookingSession(resumableSessionId)
         : await createAppointmentBookingSession({
             sponsorId,
             scheduledDate,
             scheduledTime,
             userTimezone:
               Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo',
           });
       activeBookingSessionIdRef.current = checkout.session.id;
      import('@/utils/clarityEvents')
        .then(({ logClarityEvent }) =>
          logClarityEvent(
             resumableSessionId ? 'appointment_checkout_resumed' : 'appointment_checkout_started',
            { method: 'card' }
          )
        )
        .catch(() => {});
      const publishableKey = await getStripePublishableKey();
      await initStripe({ publishableKey, urlScheme: 'padrinhoelo' });
      const init = await initPaymentSheet({
        paymentIntentClientSecret: checkout.paymentIntentClientSecret,
        merchantDisplayName: 'Padrinho',
        allowsDelayedPaymentMethods: false,
        returnURL: 'padrinhoelo://stripe-redirect',
      });
      if (init.error) {
        logCheckoutFailure('payment_sheet_init', init.error.code);
        throw new Error('PAYMENT_SHEET_INIT_FAILED');
      }
      const presented = await presentPaymentSheet();
      if (presented.error) {
        const sheetError = presented.error;
        if (sheetError.code !== 'Canceled') {
          logCheckoutFailure('payment_sheet_present', sheetError.code);
        } else {
          import('@/utils/clarityEvents')
            .then(({ logClarityEvent }) =>
              logClarityEvent('appointment_checkout_cancelled', { method: 'card' })
            )
            .catch(() => {});
        }
        if (presented.error.code === 'Canceled') {
          return;
        }
        throw new Error('PAYMENT_SHEET_FAILED');
      }
      // The PaymentSheet success only means Stripe accepted confirmation. The
       // signed webhook owns appointment creation, so poll the session briefly.
       // A mobile network failure while reading this status must not turn an
       // accepted payment into a retry prompt: the webhook may still finalize
       // the appointment while this screen is waiting.
       let statusReadFailed = false;
      for (let attempt = 0; attempt < 12; attempt += 1) {
         let status;
         try {
           status = await getAppointmentBookingSession(checkout.session.id);
         } catch {
           statusReadFailed = true;
           await new Promise((resolve) => setTimeout(resolve, 1000));
           continue;
         }
        if (status.status === 'finalized') {
          import('@/utils/clarityEvents')
            .then(({ logClarityEvent }) =>
              logClarityEvent('appointment_checkout_succeeded', { method: 'card' })
            )
            .catch(() => {});
          await navigateAfterScheduling();
          return;
        }
         if (status.status === 'failed' || status.status === 'expired' || status.status === 'canceled') {
           logCheckoutFailure('payment_finalization', status.status);
           throw new Error('PAYMENT_FINALIZATION_FAILED');
         }
        if (status.status === 'requires_refund') {
          import('@/utils/clarityEvents')
            .then(({ logClarityEvent }) =>
              logClarityEvent('appointment_checkout_refund_started', { method: 'card' })
            )
            .catch(() => {});
          setRefundOutcome('started');
          return;
        }
        if (status.status === 'refunded') {
          import('@/utils/clarityEvents')
            .then(({ logClarityEvent }) =>
              logClarityEvent('appointment_checkout_refund_completed', { method: 'card' })
            )
            .catch(() => {});
          setRefundOutcome('completed');
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
       if (statusReadFailed) {
         logCheckoutFailure('payment_finalization_status', 'STATUS_UNAVAILABLE');
       }
      throw new Error('PAYMENT_FINALIZATION_PENDING');
    } catch (error: any) {
      const message =
        error?.message === 'This time slot is no longer available'
          ? t('sponsors.errors.slotTaken')
          : error?.message === 'PAYMENT_FINALIZATION_PENDING'
            ? t('sponsors.payment.finalizationPending')
          : t('sponsors.payment.checkoutError');
      Alert.alert(t('sponsors.errors.title'), message);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const PaymentOption = ({
    method,
    label,
    icon,
    disabled = false,
  }: {
    method: PaymentMethod;
    label: string;
    icon: React.ReactNode;
    disabled?: boolean;
  }) => {
    const selected = selectedMethod === method;
    return (
      <TouchableOpacity
        style={[
          styles.paymentOption,
          selected && styles.paymentOptionSelected,
          disabled && styles.paymentOptionDisabled,
        ]}
        onPress={() => {
          if (!disabled) setSelectedMethod(method);
        }}
        activeOpacity={disabled ? 1 : 0.75}
        accessibilityRole='radio'
        accessibilityState={{ selected, disabled }}
        accessibilityLabel={disabled ? `${label}, ${t('sponsors.payment.comingSoon')}` : label}
      >
        <View style={styles.optionIcon}>{icon}</View>
        <Text style={styles.optionLabel}>{label}</Text>
        {disabled && (
          <View style={styles.comingSoonTag}>
            <Text style={styles.comingSoonText}>{t('sponsors.payment.comingSoon')}</Text>
          </View>
        )}
        <View style={[styles.radio, selected && styles.radioSelected]}>
          {selected && <View style={styles.radioDot} />}
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <PageHeader
        title={t('sponsors.payment.headerTitle')}
        onBack={() => router.back()}
        backgroundColor='#F9F8F3'
      />

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        <SponsorAppointmentSummaryCard
          sponsorName={sponsorName}
          sponsorRole={roleLabel || t('sponsors.profile.role')}
          sponsorPhotoUrl={sponsorPhotoUrl}
          scheduledForLabel={t('sponsors.profile.scheduledFor')}
          dateLabel={appointmentDateLabel}
          atLabel={t('sponsors.profile.at')}
          timeLabel={language.startsWith('pt') ? scheduledTime.replace(':', 'h') : scheduledTime}
        />

        {refundOutcome ? (
          <View style={styles.statusNotice}>
            <Text style={styles.statusNoticeTitle}>
              {refundOutcome === 'started'
                ? t('sponsors.payment.refundStartedTitle')
                : t('sponsors.payment.refundCompletedTitle')}
            </Text>
            <Text style={styles.statusNoticeText}>
              {refundOutcome === 'started'
                ? t('sponsors.payment.refundStartedMessage')
                : t('sponsors.payment.refundCompletedMessage')}
            </Text>
            <PrimaryButton
              title={t('appointments.title')}
              onPress={() => router.replace('/agendamentos')}
              style={styles.confirmButton}
            />
          </View>
        ) : (
          <View style={styles.paymentSection}>
            <Text style={styles.sectionTitle}>{t('sponsors.payment.methodTitle')}</Text>
            <View style={styles.options}>
              <PaymentOption method='card' label={t('sponsors.payment.card')} icon={<CardIcon />} />
              <PaymentOption
                method='pix'
                label={t('sponsors.payment.pix')}
                icon={<PixIcon />}
                disabled
              />
            </View>
          </View>
        )}
      </ScrollView>

      {!refundOutcome && (
        <View style={[styles.bottomSection, { paddingBottom: Math.max(insets.bottom, 12) }]}>
          <View style={styles.priceRow}>
            <Text style={styles.priceLabel}>{t('sponsors.payment.amountLabel')}</Text>
            {formattedPrice ? (
              <Text style={styles.priceValue}>{formattedPrice}</Text>
            ) : quoteLoading ? (
              <ActivityIndicator size='small' color='#004165' />
            ) : (
              <Text style={styles.priceUnavailable}>{t('sponsors.payment.priceUnavailable')}</Text>
            )}
          </View>
          <PrimaryButton
            title={t('sponsors.payment.confirmButton')}
            onPress={handleConfirm}
            loading={submitting}
            disabled={
              !isValidBookingContext ||
              !formattedPrice ||
              quoteUnavailable ||
              (quoteLoading && !routePrice)
            }
            style={styles.confirmButton}
          />
        </View>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F9F8F3' },
  content: { flex: 1 },
  contentContainer: { padding: 24, gap: 32 },
  paymentSection: { gap: 12 },
  statusNotice: {
    gap: 12,
    padding: 20,
    backgroundColor: '#E6EBF0',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#004165',
  },
  statusNoticeTitle: {
    color: '#004165',
    fontFamily: 'Inter_700Bold',
    fontSize: 17,
    lineHeight: 23,
  },
  statusNoticeText: {
    color: '#151515',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 21,
  },
  sectionTitle: {
    color: '#7E7D75',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 14,
  },
  options: { gap: 8 },
  paymentOption: {
    minHeight: 80,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 19,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#F0EEE4',
    borderRadius: 12,
  },
  paymentOptionSelected: {
    borderColor: '#004165',
    backgroundColor: '#E6EBF0',
  },
  paymentOptionDisabled: { opacity: 0.55 },
  optionIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#F9F8F3',
    alignItems: 'center',
    justifyContent: 'center',
  },
  optionLabel: {
    flex: 1,
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 20,
  },
  comingSoonTag: {
    borderRadius: 8,
    backgroundColor: '#F0EEE4',
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  comingSoonText: {
    color: '#7E7D75',
    fontFamily: 'Inter_500Medium',
    fontSize: 11,
  },
  radio: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#A19F97',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioSelected: { borderColor: '#004165', borderWidth: 3 },
  radioDot: { width: 12, height: 12, borderRadius: 6, backgroundColor: '#004165' },
  bottomSection: {
    borderTopWidth: 1,
    borderTopColor: '#F0EEE4',
    backgroundColor: '#F9F8F3',
    paddingHorizontal: 24,
    paddingTop: 16,
    gap: 16,
  },
  priceRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  priceLabel: {
    color: '#7E7D75',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 14,
  },
  priceValue: {
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 20,
  },
  priceUnavailable: {
    flexShrink: 1,
    color: '#807E79',
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    lineHeight: 16,
    textAlign: 'right',
  },
  confirmButton: { borderRadius: 12 },
});
